'use client'

import { useState } from 'react'
import { useSignIn } from '@clerk/nextjs'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Role } from '@nivex/contracts'
import {
  PASSWORD_REQUIREMENTS_HINT,
  setPasswordSchema,
  type SetPasswordFormValues,
} from '@/features/auth/schemas/auth.schema'
import { useFinalizeAsRole } from '@/features/auth/lib/use-finalize-as-role'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field'
import { PasswordInput } from '@/components/patterns/passwordInput'

const SIGN_OUT_REDIRECT: Record<Role, '/team/login' | '/login'> = {
  investor: '/login',
  staff: '/login',
  admin: '/team/login',
}

/**
 * Second of two reset-password steps — deliberately its own component, not combined with the
 * code step, so only one set of fields (password + confirm) is ever on screen at a time.
 * Shared by the admin and investor reset-password flows via the `role` prop — staff has no
 * self-serve reset, so
 * `'staff'` is never actually passed in practice, but the map above stays exhaustive.
 */
export function ResetPasswordNewPasswordStep({
  role,
  onFinalizing,
}: {
  role: Role
  onFinalizing: () => void
}) {
  const { signIn } = useSignIn()
  const finalizeAs = useFinalizeAsRole()
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const form = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordSchema),
    mode: 'onTouched',
    defaultValues: { password: '', confirmPassword: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true)
    setErrorMessage(null)
    try {
      // A forgotten password often means a prior session shouldn't be trusted either —
      // same reasoning as the admin-initiated staff reset (AdminUsersSyncService.resetPassword).
      const { error } = await signIn.resetPasswordEmailCode.submitPassword({
        password: values.password,
        signOutOfOtherSessions: true,
      })
      if (error) {
        setErrorMessage('We could not reset your password. Please try again.')
        return
      }
      if (signIn.status === 'complete') {
        // Tell the parent to stop rendering this form *before* `finalize()` starts
        // activating the session — its reactive `signIn.identifier` can otherwise read as
        // falsy for a render or two and briefly show the "no in-progress reset" empty state.
        onFinalizing()
        await finalizeAs(role, setErrorMessage, SIGN_OUT_REDIRECT[role])
        return
      }
      setErrorMessage('We could not reset your password. Please try again.')
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <FieldGroup>
      <Field>
        <FieldLabel htmlFor="reset-password">New password</FieldLabel>
        <PasswordInput
          id="reset-password"
          placeholder="Create a password"
          aria-invalid={!!form.formState.errors.password}
          {...form.register('password')}
        />
        <FieldError errors={[form.formState.errors.password]} />
        <FieldDescription>{PASSWORD_REQUIREMENTS_HINT}</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor="reset-confirm-password">Confirm password</FieldLabel>
        <PasswordInput
          id="reset-confirm-password"
          placeholder="Re-enter your password"
          aria-invalid={!!form.formState.errors.confirmPassword}
          {...form.register('confirmPassword')}
        />
        <FieldError errors={[form.formState.errors.confirmPassword]} />
      </Field>
      {errorMessage && <FieldDescription className="text-red-600">{errorMessage}</FieldDescription>}
      <Field>
        <Button type="button" onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Resetting…' : 'Reset password'}
        </Button>
      </Field>
    </FieldGroup>
  )
}
